<script setup lang="ts">
import Button from '~~/components/design-system/button.vue';
import { useSiteStore } from '~/stores/site.store';
import usePrepareMobileNumber from '~/composables/usePrepareMobileNumber';
import GenericError from '~/components/user-interface/generic-error.vue';
import { globalKeys } from 'assets/data/global-keys';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import { ECommunicationChannel as communicationChannels } from '~/interfaces/form/formBuilder';
import { type IResetPasswordRequest } from '~/interfaces/dto/auth/request';

interface IOtpOptions {
  username: string;
  otpOptions: 'sms' | 'email';
}

const { $authService, $storageService } = useServices();
const { $t, $trackEvent } = useNuxtApp();
const site = useSiteStore();
const authStore = useAuthStore();
const errorCode = ref<string>('');
const errorRef = ref<string>('');
const form = ref<IFormBuilderReference>();
const preparedMobileNumber = ref<string>('');
const prepareMobileNumber = usePrepareMobileNumber;
const otpOptions = ref<IOtpOptions>({
  username: '',
  otpOptions: 'sms',
});
const formLoading = ref<boolean>(true);
const isOtpOptionsValid = computed(
  () => !!communicationChannels[otpOptions.value.otpOptions.toUpperCase()],
);

const allowSubmit = computed(
  () =>
    isOtpOptionsValid.value && form.value?.validated && !form.value?.invalid,
);

const otpMethod = computed(
  () => communicationChannels[otpOptions.value.otpOptions.toUpperCase()],
);

const navigateToSuccess = () => {
  $storageService.setCookie({
    key: globalKeys.authKeys.passwordResetNumber,
    data: preparedMobileNumber.value,
    expiry: 7,
  });
  authStore.setPasswordResetMobileNumber(preparedMobileNumber.value);
  if (otpMethod.value === 1 || otpMethod.value === 2)
    site.activateModal('passwordResetEnterOtp');
  else site.activateModal('passwordResetEnterNewPassword');
};

const updateSettings = async () => {
  if (!allowSubmit.value) return;
  preparedMobileNumber.value = prepareMobileNumber(otpOptions.value.username);
  const { executeRecaptcha } = await useRecaptcha();
  const request: IResetPasswordRequest = {
    login: preparedMobileNumber.value,
    ...(otpMethod.value >= 1 &&
      otpMethod.value <= 2 && { contactPreferences: otpMethod.value }),
    ...(form.value?.hasRecaptcha && {
      recaptcha: await executeRecaptcha('update-password'),
    }),
  };

  formLoading.value = true;

  $storageService.setCookie({
    key: globalKeys.authKeys.passwordResetNumber,
    data: preparedMobileNumber.value,
    expiry: 7,
  });
  authStore.setPasswordResetMobileNumber(request.login);
  $trackEvent('ForgotPassword', 'REQUEST_RESET_CODE');

  try {
    const data = await $authService.passwordResetInit(request);
    if (data?.isSuccessful) {
      $trackEvent('ForgotPassword', 'REQUEST_RESET_CODE_SUCCESS');
      navigateToSuccess();
    } else {
      $trackEvent('ForgotPassword', 'REQUEST_RESET_CODE_FAILURE', {
        error: data?.error?.displayCode || data?.cause?.message,
        msisdn: preparedMobileNumber?.value,
      });
      errorCode.value = String(data?.error?.displayCode ?? 'general');
      errorRef.value = String(data?.error?.code ?? '');
    }
  } catch (error) {
    console.error(error);
    errorCode.value = String(error?.displayCode ?? 'general');
    errorRef.value = String(error?.code ?? '');
  } finally {
    formLoading.value = false;
  }
};
watchEffect(() => {
  errorCode.value = '';
  errorRef.value = '';
});
</script>
<template>
  <div :class="{ 'aspect-video': !form?.loaded }">
    <p class="text-base-priority px-4 pt-2 text-base m-0">
      {{ $t('please-enter-mobile') }}
    </p>
    <LazyUserInterfaceDynamicFormBuilder
      ref="form"
      v-model="otpOptions"
      template-name="otp options"
      :container-classes="[{ 'p-4 ': !form?.isEmpty }]"
      :element-container-classes="[{ 'relative !gap-4': !form?.isEmpty }]"
      @loaded="formLoading = false"
      @error="errorCode = $event"
      @keyup.enter.prevent="updateSettings"
    />
    <div v-if="errorCode && form" class="pb-4 px-4 flex justify-center">
      <GenericError
        class="max-w-72 mx-auto"
        state="danger"
        :reference-code="errorRef"
      >
        {{ $t(`jpc-update-password-error-${errorCode}`) }}
      </GenericError>
    </div>
    <div v-if="!form?.isEmpty" class="p-4 bg-base">
      <Button
        class="w-full justify-center"
        :disabled="!allowSubmit"
        @click="updateSettings"
      >
        {{ $t('continue') }}
      </Button>
    </div>
    <div
      v-if="errorCode && form?.isEmpty"
      class="field-col-12 mt-1 pb-4 px-4 flex justify-center"
    >
      <GenericError
        class="max-w-72 mx-auto"
        state="danger"
        :reference-code="errorRef"
      >
        {{ $t(`jpc-update-password-error-${errorCode}`) }}
      </GenericError>
    </div>
  </div>
  <div
    v-if="errorCode && !form"
    class="field-col-12 mt-1 pb-4 px-4 flex justify-center"
  >
    <GenericError
      class="max-w-72 mx-auto"
      state="danger"
      :reference-code="errorRef"
    >
      {{ $t(`jpc-update-password-otp-error-` + errorCode) }}
    </GenericError>
  </div>
  <LazyUserInterfaceGenericLoader v-if="formLoading" container />
</template>
